import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const ImageModal = ({ isOpen, imageUrl, alt, startRect, onClose }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  // Removed unused imageLoaded state
  const [isClosing, setIsClosing] = useState(false);
  // Handle opening/closing animations
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      // Small delay to ensure the component is rendered before starting animation
      setTimeout(() => setIsAnimating(true), 10);
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      setIsAnimating(false);
      // Keep component rendered during closing animation
      setTimeout(() => {
        setShouldRender(false);
          setIsClosing(false);
      }, 300);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (shouldRender) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [shouldRender, onClose]);

  // Calculate transform values for morphing animation
  const getImageTransform = () => {
    if (!startRect) {
      // No start position available - use simple scale animation
      if (isAnimating && !isClosing) {
        return {
          transform: 'translate(0, 0) scale(1)',
          opacity: 1
        };
      } else {
        return {
          transform: 'translate(0, 20px) scale(0.9)',
          opacity: 0
        };
      }
    }

    // Calculate transform values based on original position
    const windowCenterX = window.innerWidth / 2;
    const windowCenterY = window.innerHeight / 2;
    
    const translateX = startRect.left + startRect.width / 2 - windowCenterX;
    const translateY = startRect.top + startRect.height / 2 - windowCenterY;
    
    // Calculate scale based on original size vs modal optimal size
    const maxModalWidth = window.innerWidth * 0.9;
    const maxModalHeight = window.innerHeight * 0.9;
    
    // Estimate what the modal image size would be while maintaining aspect ratio
    const imageAspectRatio = startRect.width / startRect.height;
    let modalWidth, modalHeight;
    
    if (imageAspectRatio > maxModalWidth / maxModalHeight) {
      // Image is wider - constrain by width
      modalWidth = Math.min(maxModalWidth, startRect.width * 3);
      modalHeight = modalWidth / imageAspectRatio;
    } else {
      // Image is taller - constrain by height  
      modalHeight = Math.min(maxModalHeight, startRect.height * 3);
      modalWidth = modalHeight * imageAspectRatio;
    }
    
    const scale = Math.min(startRect.width / modalWidth, startRect.height / modalHeight, 1);
    
    if (isAnimating && !isClosing) {
      // Opened state - centered and full size
      return {
        transform: 'translate(0, 0) scale(1)',
        opacity: 1
      };
    } else {
      // Starting/closing position - at original location and size
      return {
        transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
        opacity: 1
      };
    }
  };

  if (!shouldRender) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ease-out ${
        isAnimating && !isClosing
          ? 'bg-black bg-opacity-75 opacity-100' 
          : 'bg-black bg-opacity-0 opacity-0'
      }`}
      onClick={onClose}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all duration-300 ${
            isAnimating && !isClosing ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
          title="Close (Esc)"
        >
          <X size={24} />
        </button>
        
        <img
          src={imageUrl}
          alt={alt}
          className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl transition-all duration-300 ease-out"
          style={{
            width: 'auto',
            height: 'auto',
            ...getImageTransform(),
          }}
          onClick={(e) => e.stopPropagation()}
          onLoad={() => {}}
        />
      </div>
    </div>
  );
};

export default ImageModal;